import { type JSONContent } from '@tiptap/core';
import { createContext, memo, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { type WorkspaceAgentGuidedFlowSubmissionParams } from '@air/analytics';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type WorkspaceAgentStructuredDisplay } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentStructuredDisplay';
import { WorkspaceAgentComposerLockProvider } from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerLockProvider';
import { isDevOrTestStage } from '~/swr-hooks/utils';

const noop = () => undefined;

export type WorkspaceAgentComposerSeedSource =
  | 'actions_menu'
  | 'announcement'
  | 'board_menu'
  | 'edit_tags_modal'
  | 'guided_flow'
  | 'hero_chip'
  | 'marketing_url'
  | 'selection_menu';

export type WorkspaceAgentComposerSeedBoardPill = {
  boardId: string;
  /** Composer chip label. Omit when the seed only has an id; the chip falls back to "Board". */
  title?: string;
};

type WorkspaceAgentComposerSeedSharedOptions = {
  /** The Actions menu task the prompt came from, reported when the prompt is sent. */
  actionsMenuTaskId?: string;
  /** Inserted as a cited board pill at the start of the prompt, so the agent receives it as a named reference. */
  boardPill?: WorkspaceAgentComposerSeedBoardPill;
  /**
   * TipTap document to insert instead of plain text from `prompt`. Callers that need a citation
   * build that document themselves — the seed does not splice mentions.
   */
  jsonContent?: JSONContent[];
  sendImmediately?: boolean;
  structuredDisplay?: WorkspaceAgentStructuredDisplay;
  /** Set when the seed is tied to a workspace (hero queue, marketing). */
  workspaceId?: string;
};

/** A guided-flow seed always carries the edit the wizard committed. */
export type WorkspaceAgentComposerSeedOptions =
  | (WorkspaceAgentComposerSeedSharedOptions & {
      guidedFlowSubmission: WorkspaceAgentGuidedFlowSubmissionParams;
      source: 'guided_flow';
    })
  | (WorkspaceAgentComposerSeedSharedOptions & {
      source?: Exclude<WorkspaceAgentComposerSeedSource, 'guided_flow'>;
    });

type WorkspaceAgentComposerSeedBase = {
  actionsMenuTaskId?: string;
  boardPill?: WorkspaceAgentComposerSeedBoardPill;
  jsonContent?: JSONContent[];
  prompt: string;
  sendImmediately: boolean;
  structuredDisplay?: WorkspaceAgentStructuredDisplay;
  workspaceId?: string;
};

export type WorkspaceAgentComposerSeed =
  | (WorkspaceAgentComposerSeedBase & {
      guidedFlowSubmission: WorkspaceAgentGuidedFlowSubmissionParams;
      source: 'guided_flow';
    })
  | (WorkspaceAgentComposerSeedBase & {
      source?: Exclude<WorkspaceAgentComposerSeedSource, 'guided_flow'>;
    });

export type WorkspaceAgentComposerSeedApi = {
  clearSeed: () => void;
  seed: WorkspaceAgentComposerSeed | null;
  seedComposer: (prompt: string, options?: WorkspaceAgentComposerSeedOptions) => void;
};

const defaultComposerSeedValue: WorkspaceAgentComposerSeedApi = {
  clearSeed: noop,
  seed: null,
  seedComposer: noop,
};

const WorkspaceAgentComposerSeedContext = createContext<WorkspaceAgentComposerSeedApi>(defaultComposerSeedValue);

export const useWorkspaceAgentComposerSeed = () => {
  const context = useContext(WorkspaceAgentComposerSeedContext);

  if (context === defaultComposerSeedValue) {
    const error = new Error('useWorkspaceAgentComposerSeed must be used within WorkspaceAgentComposerSeedProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({ error, context: 'Workspace agent composer seed context not found' });
  }

  return context;
};

/** Zero state and composer are siblings — seed crosses that gap without either owning the other. */
export const WorkspaceAgentComposerSeedProvider = memo(({ children }: { children: ReactNode }) => {
  const [seed, setSeed] = useState<WorkspaceAgentComposerSeed | null>(null);

  const clearSeed = useCallback(() => setSeed(null), []);
  const seedComposer = useCallback((prompt: string, options?: WorkspaceAgentComposerSeedOptions) => {
    if (options?.source === 'guided_flow') {
      setSeed({
        boardPill: options.boardPill,
        guidedFlowSubmission: options.guidedFlowSubmission,
        jsonContent: options.jsonContent,
        prompt,
        sendImmediately: options.sendImmediately ?? false,
        source: 'guided_flow',
        structuredDisplay: options.structuredDisplay,
        workspaceId: options.workspaceId,
      });

      return;
    }

    setSeed({
      actionsMenuTaskId: options?.actionsMenuTaskId,
      boardPill: options?.boardPill,
      jsonContent: options?.jsonContent,
      prompt,
      sendImmediately: options?.sendImmediately ?? false,
      source: options?.source,
      structuredDisplay: options?.structuredDisplay,
      workspaceId: options?.workspaceId,
    });
  }, []);

  const contextValue = useMemo(() => ({ clearSeed, seed, seedComposer }), [clearSeed, seed, seedComposer]);

  return (
    <WorkspaceAgentComposerSeedContext.Provider value={contextValue}>
      <WorkspaceAgentComposerLockProvider>{children}</WorkspaceAgentComposerLockProvider>
    </WorkspaceAgentComposerSeedContext.Provider>
  );
});

WorkspaceAgentComposerSeedProvider.displayName = 'WorkspaceAgentComposerSeedProvider';
